import Cookies from 'js-cookie';

// Font size scale multipliers
export const FONT_SIZE_SCALES = {
    small: 0.85,
    medium: 1.0,
    large: 1.15,
    extraLarge: 1.3
};

// Base font sizes for different text types (in px)
export const BASE_FONT_SIZES = {
    // Headers and titles
    appTitle: 28,           // App bar title "ALL DAY"
    sectionHeader: 20,      // Section headers in menu
    mealTitle: 16,          // Meal name/title

    // Body text
    bodyText: 14,           // Regular body text
    subtitle: 12,           // Subtitles, descriptions
    caption: 11,            // Small captions, helper text

    // Prices and numbers
    priceMain: 16,          // Main price display
    priceSmall: 14,         // Small price text

    // Buttons and controls
    buttonText: 14,         // Button labels
    tabText: 14,            // Tab labels

    // Special elements
    badge: 12,              // Badge numbers
    tooltip: 11,            // Tooltip text
};

// Cookie settings
const FONT_SIZE_COOKIE_NAME = 'quickOrderingFontSize';
const COOKIE_EXPIRES_DAYS = 365; // 1 year

/**
 * Get the current font size setting from cookies
 * @returns {string} Font size setting ('small', 'medium', 'large', 'extraLarge')
 */
export const getFontSizeSetting = () => {
    const savedSetting = Cookies.get(FONT_SIZE_COOKIE_NAME);
    return savedSetting && FONT_SIZE_SCALES[savedSetting] ? savedSetting : 'medium';
};

/**
 * Save font size setting to cookies
 * @param {string} size - Font size setting ('small', 'medium', 'large', 'extraLarge')
 */
export const setFontSizeSetting = (size) => {
    if (FONT_SIZE_SCALES[size]) {
        Cookies.set(FONT_SIZE_COOKIE_NAME, size, { expires: COOKIE_EXPIRES_DAYS });
    }
};

/**
 * Get the scale multiplier for the current font size setting
 * @returns {number} Scale multiplier
 */
export const getFontSizeScale = () => {
    const setting = getFontSizeSetting();
    return FONT_SIZE_SCALES[setting];
};

/**
 * Apply font size scale to a base font size
 * @param {string|number} baseFontSize - Base font size (e.g., '16px', '1.2rem', 16)
 * @returns {string} Scaled font size
 */
export const scaleFontSize = (baseFontSize) => {
    const scale = getFontSizeScale();

    if (typeof baseFontSize === 'string') {
        // Handle rem units
        if (baseFontSize.includes('rem')) {
            const value = parseFloat(baseFontSize);
            return `${(value * scale).toFixed(2)}rem`;
        }
        // Handle px units
        if (baseFontSize.includes('px')) {
            const value = parseFloat(baseFontSize);
            return `${Math.round(value * scale)}px`;
        }
        // Handle em units
        if (baseFontSize.includes('em')) {
            const value = parseFloat(baseFontSize);
            return `${(value * scale).toFixed(2)}em`;
        }
    }

    // Handle numeric values (assume px)
    if (typeof baseFontSize === 'number') {
        return `${Math.round(baseFontSize * scale)}px`;
    }

    // Return original if can't parse
    return baseFontSize;
};

/**
 * Get scaled font size for a specific text type
 * @param {string} textType - Type of text (e.g., 'mealTitle', 'subtitle', 'bodyText')
 * @returns {string} Scaled font size in px
 */
export const getScaledFontSize = (textType) => {
    const baseSize = BASE_FONT_SIZES[textType];
    if (!baseSize) {
        console.warn(`Unknown text type: ${textType}`);
        return '14px'; // fallback
    }

    const scale = getFontSizeScale();
    return `${Math.round(baseSize * scale)}px`;
};

/**
 * Get all scaled font sizes for the current setting
 * @returns {Object} Object with all scaled font sizes
 */
export const getAllScaledFontSizes = () => {
    const scale = getFontSizeScale();
    const scaledSizes = {};

    for (const [key, baseSize] of Object.entries(BASE_FONT_SIZES)) {
        scaledSizes[key] = `${Math.round(baseSize * scale)}px`;
    }

    return scaledSizes;
};

/**
 * Get all available font size options with labels
 * @returns {Array} Array of font size options
 */
export const getFontSizeOptions = () => [
    { value: 'small', label: '小', scale: FONT_SIZE_SCALES.small },
    { value: 'medium', label: '中', scale: FONT_SIZE_SCALES.medium },
    { value: 'large', label: '大', scale: FONT_SIZE_SCALES.large },
    { value: 'extraLarge', label: '特大', scale: FONT_SIZE_SCALES.extraLarge }
];

/**
 * Create CSS styles object with scaled font sizes
 * @param {Object} fontSizeMap - Map of style keys to text types
 * @returns {Object} CSS styles object
 */
export const createFontSizeStyles = (fontSizeMap) => {
    const styles = {};

    for (const [styleKey, textType] of Object.entries(fontSizeMap)) {
        styles[styleKey] = {
            fontSize: getScaledFontSize(textType)
        };
    }

    return styles;
};
